import { createSlice } from '@reduxjs/toolkit';
import { createSliceHook, makeActionCreator } from '../helpers';
import type {
  ClinicalTrialSiteRecipientsState,
  DownloadBulkTemplateAction,
  ExportSiteRecipientsAction,
  FetchSiteRecipientsAction,
  FetchSiteRecipientsSuccessAction,
  UploadBulkRecipientsAction,
  UploadBulkRecipientsResultAction,
} from './clinicalTrialSiteRecipients.types';

const initialState: ClinicalTrialSiteRecipientsState = {
  loading: false,
  error: undefined,
  data: [],
  uploading: false,
  uploadError: undefined,
  uploadResult: undefined,
  templateDownloading: false,
  exporting: false,
};

const { actReq, actResolve, act } =
  makeActionCreator<ClinicalTrialSiteRecipientsState>();

export const clinicalTrialSiteRecipientsSlice = createSlice({
  name: 'clinicalTrialSiteRecipients',
  initialState,
  reducers: {
    fetchSiteRecipients:
      actReq<FetchSiteRecipientsAction['payload']>('loading'),
    fetchSiteRecipientsSuccess: actResolve<
      FetchSiteRecipientsSuccessAction['payload']
    >('data', 'loading', 'error'),
    /**
     * Clears `data` too — `actReject` would leave the previous site's
     * recipients on screen looking current after a failed refresh.
     */
    fetchSiteRecipientsFailure: act<Error>(error => ({
      error,
      loading: false,
      data: [],
    })),
    /**
     * Cleared selection resets everything — the prompt to pick a site must not
     * render above the last site's recipients.
     */
    resetSiteRecipients: act<void>(() => initialState),
    /**
     * Longhand, not `actReq` — that hardcodes `error: undefined` and would
     * clear the fetch error behind the tab's `LOAD_ERROR_MESSAGE`.
     */
    uploadBulkRecipients: act<UploadBulkRecipientsAction['payload']>(() => ({
      uploading: true,
      uploadError: undefined,
      uploadResult: undefined,
    })),
    uploadBulkRecipientsSuccess: act<
      UploadBulkRecipientsResultAction['payload']
    >(uploadResult => ({
      uploading: false,
      uploadError: undefined,
      uploadResult,
    })),
    /**
     * Request worked, rows refused — neither success nor failure (D7-7702).
     * `uploadError` stays undefined; it is reserved for a thrown request.
     */
    uploadBulkRecipientsRejected: act<
      UploadBulkRecipientsResultAction['payload']
    >(uploadResult => ({
      uploading: false,
      uploadError: undefined,
      uploadResult,
    })),
    uploadBulkRecipientsFailure: act<Error>(uploadError => ({
      uploading: false,
      uploadError,
      uploadResult: undefined,
    })),
    /** A flag only — the saga toasts a failure and the link re-enables. */
    downloadBulkTemplate: act<DownloadBulkTemplateAction['payload']>(() => ({
      templateDownloading: true,
    })),
    downloadBulkTemplateSuccess: act<void>(() => ({
      templateDownloading: false,
    })),
    downloadBulkTemplateFailure: act<Error>(() => ({
      templateDownloading: false,
    })),
    /** A flag only, as above — the saga toasts a failure and the button re-enables. */
    exportSiteRecipients: act<ExportSiteRecipientsAction['payload']>(() => ({
      exporting: true,
    })),
    exportSiteRecipientsSuccess: act<void>(() => ({
      exporting: false,
    })),
    exportSiteRecipientsFailure: act<Error>(() => ({
      exporting: false,
    })),
  },
});

// Named exports for the terminal upload actions — the drawer subscribes to
// them directly via `subscribeToActions`.
export const {
  uploadBulkRecipientsSuccess,
  uploadBulkRecipientsRejected,
  uploadBulkRecipientsFailure,
} = clinicalTrialSiteRecipientsSlice.actions;

export const actions = {
  ...clinicalTrialSiteRecipientsSlice.actions,
};

export const useClinicalTrialSiteRecipientsSlice = createSliceHook(
  clinicalTrialSiteRecipientsSlice.name,
  actions
);
